import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import S3Upload from "react-s3-uploader/s3upload";
import AuthService from "services/auth.service";
import shortid from "shortid";
import { createWidget, copyWidgets, cutWidgets, actionGroup, deleteWidgets, updateBulkWidgetProperty, updateWidgetProperty, copyPasteAnimation, arrangeWidget } from "actions/editor";
import { initiateMediaUpload, updateMediaUploadProgress, uploadMedia } from "actions/media";
import { getPositionForNewWidget } from "components/Editor/helper/layoutManager";
import { FailureToast, SuccessToast } from "components/Toast";

import { EditorContext } from "components/Editor";
import StoryboardContext from "components/Storyboard/context";
import { useSelectedWidgetSelector } from "components/Editor/hooks/useEditorSession";

import _ from "lodash";
import { Frame } from "scenejs";
import { toast } from "react-toastify";
import { ChunkProgressModal } from "./chunkProgressModal";
import { FigmaImportFontModal } from "pages/storyboards/figma";

import { ANCHOR, ASSET, BUTTON, CAROUSEL, CAROUSEL_CARD, CONTENT, CONTENT_CARD, CONTENT_FILTER, CONTENT_FILTER_CONTAINER, CONTENT_SEARCH, IMAGE, LINE, LIVE_ELEMENT, MENU, SECTION, SECTION_COLUMN, SEEK, SHAPE, VIDEO, HOTSPOT, TEXT, MENU_CONTAINER, MENU_DRAGGABLE_CONTAINER, PAGE } from "constants/widgetTypes";
import { PAGE_CONTAIN_STORYBOARDS, SEGMENT_STORYBOARDS, SLIDE_STORYBOARDS, STORYBOARD_TYPES } from "constants/storyboardType";
import { clearFigmaClipboard, getFigmaExportKey } from "./figma-utils";
import figmaSession from "./figma-session";

const API_URL = `${process.env.REACT_APP_PAPERFLITE_API_URL}/api/2.0`;

const clipboardManagerState = {
  desktopParentLayout: null,
  mobileParentLayout: null,
  defaultParent: null
};

const chunkAccumulator = {};

export default function ClipboardManager(props) {
  const dispatch = useDispatch();
  const store = useStore();
  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext?.editorSession?.id;
  const storyboardContext = React.useContext(StoryboardContext);
  const storyboardType = _.get(storyboardContext, 'type');

  const lastCallRef = useRef(null);
  const lastPasteCallRef = useRef(null);
  const isChunkImportActiveRef = useRef(false);
  const figmaDispatchInProgressRef = useRef(false);
  const pendingFigmaDataRef = useRef(null);
  const showFontsModalRef = useRef(false);
  const pasteStateReference = React.useRef({
    page: null,
    scrollPosition: null,
    offset: 1
  });
  const workspace = useSelector(state => _.get(state, 'workspaces.workspace.item', {}));
  const entityType = useSelector(state => _.get(state, `${editorSession}.present.entityType`));
  const presentWidgets =  useSelector(state => _.get(state, `${editorSession}.present.widgets`)) || [];
  const breakpoint = useSelector(state => _.get(state, `${editorContext.editorSession.id}.present.breakpoint`));
  const uploads = useSelector(state => _.get(state, 'media.uploads')) || [];
  const [showChunkModal, setShowChunkModal] = useState(false);
  const [chunkProgress, setChunkProgress] = useState({ current: 0, total: 0 });
  const [showFontsModal, setShowFontsModal] = useState(false);
  const [pendingFigmaData, setPendingFigmaData] = useState(null);
  const [extractedFonts, setExtractedFonts] = useState([]);
  const [fontMappings, setFontMappings] = useState({});

  const activePageSection = useSelector(state => {
    if(SEGMENT_STORYBOARDS.includes(storyboardType) && entityType!="GATING") {
      const selectedPage = _.get(state, `${editorSession}.present.selectedPage`, null);
      const activePage = _.get(state, `${editorSession}.present.activeSegment`, null);
      return selectedPage !== activePage ? activePage : selectedPage;
    }

    return _.get(state, `${editorSession}.present.interactive.active`, null);
  });
  const defaultParent = activePageSection;
  const parentWidget = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || []
    return widgets.find(widget => {
      return widget.uid === defaultParent
    });
  });
  const selectedWidgets = useSelectedWidgetSelector(editorSession);
  const pageId = editorSession?.split("_")?.[1] || '';
  const figmaImportValidations = SEGMENT_STORYBOARDS.includes(storyboardType) &&
                                 entityType === "PAGE";

  useEffect(() => {
    pendingFigmaDataRef.current = pendingFigmaData;
  }, [pendingFigmaData]);

  useEffect(() => {
    showFontsModalRef.current = showFontsModal;
  }, [showFontsModal]);

  useEffect(() => {
    editorContext.editorSession.eventBus.on('copy', handleCopy);
    editorContext.editorSession.eventBus.on('cut', handleCut);
    editorContext.editorSession.eventBus.on('paste', handlePaste);
    editorContext.editorSession.eventBus.on('pasteMedia', handlePasteMedia);
    editorContext.editorSession.eventBus.on('dropMedia', handlePasteMedia);
    editorContext.editorSession.eventBus.on('copyAnimation', handleCopyAnimation);
    editorContext.editorSession.eventBus.on('pasteAnimation', handlePasteAnimation);

    return () => {
      editorContext.editorSession.eventBus.off('copy', handleCopy);
      editorContext.editorSession.eventBus.off('cut', handleCut);
      editorContext.editorSession.eventBus.off('paste', handlePaste);
      editorContext.editorSession.eventBus.off('pasteMedia', handlePasteMedia);
      editorContext.editorSession.eventBus.off('dropMedia', handlePasteMedia);
      editorContext.editorSession.eventBus.off('copyAnimation', handleCopyAnimation);
      editorContext.editorSession.eventBus.off('pasteAnimation', handlePasteAnimation);
    }

  }, [activePageSection]);

  useEffect(() => {
    const desktopParentLayout = _.get(parentWidget, 'properties.desktop.layout');
    const mobileParentLayout = _.get(parentWidget, 'properties.mobile.layout');

    if (defaultParent != null && desktopParentLayout != null && mobileParentLayout != null) {
      clipboardManagerState.desktopParentLayout = desktopParentLayout;
      clipboardManagerState.mobileParentLayout = mobileParentLayout;
      clipboardManagerState.defaultParent = defaultParent;
    }
    
  }, [defaultParent, parentWidget, editorSession]);

  useEffect(() => {
    if (uploads && uploads.length > 0) {
      uploads.forEach(upload => {
        if (upload.error) {
          const errorDescription = 'Unable to upload the file, Please try again.';
        
          if (!toast.isActive(`upload-error-${upload.id}-toast`)) {
            toast(
              <FailureToast
                heading="Upload Failed"
                description={errorDescription}
              />,
              {
                type: toast.TYPE.DEFAULT,
                position: toast.POSITION.BOTTOM_RIGHT,
                autoClose: 3000,
                toastId: `upload-error-${upload.id}-toast`
              }
            );
          }
          dispatch(deleteWidgets(editorSession, [`widget_loading_${upload.id}`]));
        }
      });
    }
  }, [uploads, editorSession]);

  const handleCopy = (e) => {
    const request = prepareCopyRequest();
    dispatch(copyWidgets(editorContext.editorSession.id, request));
    pasteStateReference.current = {
      page: activePageSection,
      scrollPosition: request.scrollPosition,
      offset: 1
    };
  }

  const handleCut = (e) => {
    const request = prepareCopyRequest();
    dispatch(cutWidgets(editorContext.editorSession.id, request));
    pasteStateReference.current = {
      page: activePageSection,
      scrollPosition: request.scrollPosition,
      offset: 1
    };
  }

  const handleFileCheck = (file,next) => {
    next(file);
  }

  const getDimensions = (file) => {
    return new Promise((resolve) => {
      if (file.type.startsWith('image/')) {
        const img = new Image();
        img.onload = () => {
          resolve({ width: img.naturalWidth, height: img.naturalHeight });
          URL.revokeObjectURL(img.src);
        };
        img.onerror = () => resolve({ width: 600, height: 600 });
        img.src = URL.createObjectURL(file);
      } else {
        resolve({ width: 600, height: 600 }); 
      }
    });
  };

  const handleUploadComplete = async (info, file, loadingWidgetId, desktopPosition, mobilePosition, item = null, pageWidgetUid = null) => {
    const formats = ['image/jpg', 'image/jpeg', 'image/png', 'image/svg', 'image/bmp', 'image/gif', 'image/svg+xml'];
  
    if (info && formats.includes(file.type)) {
      const dimensions = await getDimensions(file);
      const width = dimensions.width;
      const height = dimensions.height;
  
      const request = {
        fileName: file.name,
        contentType: file.type,
        signedUrl: info.signedUrl,
        width: width,
        height: height,
        storyboard: _.get(storyboardContext, 'id'),
      };

      if (item) {
        handleFigmaMediaUpload(request, file, loadingWidgetId, item, pageWidgetUid);
      }
      else {
        handleUploadMedia(request, file, loadingWidgetId, desktopPosition, mobilePosition);
      }
    } else {
      setTimeout(() => {
        dispatch(deleteWidgets(editorSession, [loadingWidgetId]));
      }, 0);
    }
  };

  const handleFigmaMediaUpload = (request, file, loadingWidgetId, item, pageWidgetUid) => {
    const siblingCount = (store.getState()[editorSession]?.present?.widgets || []).filter(
      (w) => w.parent === pageWidgetUid
    ).length;
    dispatch(uploadMedia(file.id, request, (response) => {
      if (response) {
        item.content = response;
        dispatch(createWidget(editorSession, [item], pageWidgetUid));
        dispatch(arrangeWidget(editorSession,{uid: item.uid, parent: pageWidgetUid,}, siblingCount + item.order));
      }
  
      setTimeout(() => {
        dispatch(deleteWidgets(editorSession, [loadingWidgetId]));
      }, 10);
    }, true));
  };

  const handleCreateImage = (medias, specificPositions = null) => {
    actionGroup.start();
    let successfulUploads = 0;

    medias.forEach((media, index) => {
      const naturalWidth = _.get(media, 'width') || 600;
      const naturalHeight = _.get(media, 'height') || 600;
  
      const desktopWidth = Math.min(600, naturalWidth);
      const desktopHeight = desktopWidth * naturalHeight / naturalWidth;
      const mobileWidth = Math.min(300, naturalWidth);
      const mobileHeight = mobileWidth * naturalHeight / naturalWidth;
  
      let desktopPosition, mobilePosition;
  
      if (specificPositions && specificPositions[index]) {
        desktopPosition = specificPositions[index].desktop;
        mobilePosition = specificPositions[index].mobile;
      } else {
        desktopPosition = getPositionForNewWidget(editorSession, clipboardManagerState.desktopParentLayout, { width: desktopWidth, height: desktopHeight });
        mobilePosition = getPositionForNewWidget(editorSession, clipboardManagerState.mobileParentLayout, { width: 300, height: desktopHeight });
      }
      
      let widget = null;
      if (media.type === 'image') {
        widget = {
          type: IMAGE,
          name: "Image",
          properties: {
            desktop: {
              visible: true,
              layout: {
                transform: {
                  translate: `${desktopPosition.x}px, ${desktopPosition.y}px`,
                  rotate: "0deg"
                },
                width: `${desktopWidth}px`,
                height: `${desktopHeight}px`
              },
              crop: null,
              alt: null,
              animation: null,
              border: null,
              position: null,
              shadow: null
            },
            mobile: {
              visible: true,
              layout: {
                transform: {
                  translate: `${mobilePosition.x}px, ${mobilePosition.y}px`,
                  rotate: "0deg"
                },
                width: `${mobileWidth}px`,
                height: `${mobileHeight}px`
              },
              crop: null,
              alt: null,
              animation: null,
              border: null,
              position: null,
              shadow: null
            }
          },
          content: media
        };
      }
      
      if (widget) {
        dispatch(createWidget(editorSession, [widget], clipboardManagerState.defaultParent));
        successfulUploads++;
      }
    });

    const isToastAlreadyActive = toast.isActive('image_uploaded_toast');
    if (!isToastAlreadyActive && uploads.length == 0 && successfulUploads > 0) {
      toast(
        <SuccessToast
          heading={`Success`}
          description={`Image Uploaded successfully`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
          toastId: 'image_uploaded_toast'
        })
    }

    actionGroup.end();
  };

  const handleUploadMedia = (request, file, loadingWidgetId, desktopPosition, mobilePosition) => {
    dispatch(uploadMedia(file.id, request, (response) => {
      if (response) {
        const specificPositions = [{
          desktop: desktopPosition,
          mobile: mobilePosition
        }];
        handleCreateImage([response], specificPositions);
      }
  
      setTimeout(() => {
        dispatch(deleteWidgets(editorSession, [loadingWidgetId]));
      }, 10);
    }, true));
  };

  const removeSpecialCharactersFromName = (name)=>{
    return name.replace(/\u202F/g, '');
  }

  const handleUploadProgress = (progress, textState, file) => {
    dispatch(updateMediaUploadProgress(file.id, progress));
  }

  const handleUploadStart = (instances) => {
    instances.forEach(instance => {
      const upload = {
        id: instance.files[0].id,
        name: instance.files[0].name,
        type: instance.files[0].type,
        size: (instance.files[0].size / 1048576),
        progress: 0,
        instance: instance
      }

      dispatch(initiateMediaUpload(upload, false, true));
    });

  }

  const handlePasteMedia = async (instances) => {
    const currentTime = Date.now();
    if (lastCallRef.current && currentTime - lastCallRef.current < 100) {
      return;
    }
    lastCallRef.current = currentTime;
  
    const files = instances.files || [];
    
    if (files.length === 0) {
      return;
    }
  
    const imageFormats = ['image/jpg', 'image/jpeg', 'image/png', 'image/svg', 'image/bmp', 'image/gif', 'image/svg+xml'];
    const validImageFiles = files.filter(file => imageFormats.includes(file.type));
    const invalidFiles = files.filter(file => !imageFormats.includes(file.type));

    if (invalidFiles.length > 0) {
      invalidFiles.forEach(file => {
        const errorDescription = `${file.type || 'Unknown'} file type is not supported`;
      
        if (!toast.isActive(`unsupported-${file.type}-toast`)) {
          toast(
            <FailureToast
              heading="Failure"
              description={errorDescription}
            />,
            {
              type: toast.TYPE.DEFAULT,
              position: toast.POSITION.BOTTOM_RIGHT,
              autoClose: 3000,
              toastId: `unsupported-${file.type}-toast`
            }
          );
        }
      });
    }

    if (validImageFiles.length === 0) {
      return;
    }
  
    const scrollContainer = document.querySelector(`#${editorContext.editorSession.id} .editor_widget_scroll_container`);
    const currentScrollPosition = {
      top: scrollContainer.scrollTop,
      left: scrollContainer.scrollLeft
    };
  
    var lastPastedPage = pasteStateReference.current.page;
    var offset = pasteStateReference.current.offset;
    if(lastPastedPage != activePageSection || viewportPositionChanged(currentScrollPosition, pasteStateReference.current.scrollPosition)){
      offset = 0;
    }
  
    const processedUploads = [];
    
    for (let i = 0; i < validImageFiles.length; i++) {
      const file = validImageFiles[i];
      file.id = shortid();
      
      const dimensions = await getDimensions(file);
  
      const naturalWidth = dimensions.width;
      const naturalHeight = dimensions.height;
      const desktopWidth = Math.min(600, naturalWidth);
      const desktopHeight = desktopWidth * naturalHeight / naturalWidth;
      const mobileWidth = Math.min(300, naturalWidth);
      const mobileHeight = mobileWidth * naturalHeight / naturalWidth;
  
      const desktopPosition = getPositionForNewWidget(
        editorSession, 
        clipboardManagerState.desktopParentLayout, 
        { width: desktopWidth, height: desktopHeight }
      );
      const mobilePosition = getPositionForNewWidget(
        editorSession, 
        clipboardManagerState.mobileParentLayout, 
        { width: 300, height: desktopHeight }
      );
  
      const totalOffset = (offset + i) * 20;
      desktopPosition.x += totalOffset;
      desktopPosition.y += totalOffset;
      mobilePosition.x += totalOffset;
      mobilePosition.y += totalOffset;
  
      const loadingWidgetId = `widget_loading_${file.id}`;
      const updatedName = removeSpecialCharactersFromName(file.name);
      
      const loadingWidget = {
        type: "cleverstory.v2.widgets.image",
        name: "Image",
        properties: {
          desktop: {
            visible: true,
            layout: {
              transform: {
                translate: `${desktopPosition.x}px, ${desktopPosition.y}px`,
                rotate: "0deg"
              },
              width: `${desktopWidth}px`,
              height: `${desktopHeight}px`
            },
            crop: null,
            alt: null,
            animation: null,
            border: null,
            position: null,
            shadow: null
          },
          mobile: {
            visible: true,
            layout: {
              transform: {
                translate: `${mobilePosition.x}px, ${mobilePosition.y}px`,
                rotate: "0deg"
              },
              width: `${mobileWidth}px`,
              height: `${mobileHeight}px`
            },
            crop: null,
            alt: null,
            animation: null,
            border: null,
            position: null,
            shadow: null
          }
        },
        content: null,
        uid: loadingWidgetId,
        parent: `${clipboardManagerState.defaultParent}`
      };
  
      dispatch(createWidget(editorSession, [loadingWidget], clipboardManagerState.defaultParent, true, true));
  
      const s3Uploader = new S3Upload({
        signingUrl: "/file_upload/initiate",
        signingUrlMethod: "POST",
        signingUrlHeaders: {
          Authorization: `Bearer ${AuthService.getAccessToken()}`
        },
        server: API_URL,
        scrubFilename: (filename) => {
          let name = removeSpecialCharactersFromName(filename);
          name = name.replace(/[^\w\d_\-.\s]+/ig, '');
          return name;
        },
        signingUrlQueryParams: { 'originalName': encodeURIComponent(updatedName) },
        contentDisposition: 'auto',
        preprocess: handleFileCheck,
        uploadRequestHeaders: {},
        onFinishS3Put: (info, uploadedFile) => handleUploadComplete(info, uploadedFile, loadingWidgetId, desktopPosition, mobilePosition),
        onProgress: handleUploadProgress,
        onError: (error) => {
          const errorDescription = 'Unable to upload the file, Please try again, Please try again.';

          if (!toast.isActive(`upload-error-${file.id}-toast`)) {
            toast(
              <FailureToast
                heading="Upload Failed"
                description={errorDescription}
              />,
              {
                type: toast.TYPE.DEFAULT,
                position: toast.POSITION.BOTTOM_RIGHT,
                autoClose: 3000,
                toastId: `upload-error-${file.id}-toast`
              }
            );
          }

          setTimeout(() => {
            dispatch(deleteWidgets(editorSession, [loadingWidgetId]));
          }, 0);
        },
        files: [file]
      });
  
      processedUploads.push(s3Uploader);
    }
  
    pasteStateReference.current = {
      page: activePageSection,
      scrollPosition: currentScrollPosition,
      offset: offset + validImageFiles.length
    };
  
    handleUploadStart(processedUploads);
  };

  const handlePaste = async () => {
    const currentTime = Date.now();
    if (lastPasteCallRef.current && currentTime - lastPasteCallRef.current < 300) {
      return;
    }
    lastPasteCallRef.current = currentTime;

    let text = null;
    let data = null;

    try {
      text = await navigator.clipboard.readText();
      data = JSON.parse(text);
    } catch (_) {
    }

    const isFigmaData = data && data.nodes && Array.isArray(data.nodes) && figmaImportValidations;

    if (isFigmaData) {
      const isChunked  = typeof data.chunkIndex === 'number' && typeof data.totalChunks === 'number';
      const exportKey  = getFigmaExportKey(data);
      const currentSbId = _.get(storyboardContext, 'id');

      if (figmaSession.dispatchInProgress || pendingFigmaDataRef.current || showFontsModalRef.current) {
        toast(
          <FailureToast
            heading="Import in Progress"
            description="An import is already running. Please wait for it to complete."
          />,
          { position: toast.POSITION.BOTTOM_RIGHT, autoClose: 3500 }
        );
        return;
      }
  
      if (figmaSession.isBlockedByDifferentStoryboard(currentSbId)) {
        toast(
          <FailureToast
            heading="Import locked to another storyboard"
            description="A chunk import is in progress in another storyboard. Finish it there first."
          />,
          { position: toast.POSITION.BOTTOM_RIGHT, autoClose: 3500 }
        );
        return;
      }
      if (figmaSession.isBlockedByDifferentExport(exportKey)) {
        Object.keys(chunkAccumulator).forEach(k => delete chunkAccumulator[k]);
        figmaSession.endChunkSession();
        isChunkImportActiveRef.current = false;
        setShowChunkModal(false);
        setChunkProgress({ current: 0, total: 0 });
  
        toast(
          <SuccessToast
            heading="New import started"
            description="Previous multi-part import was reset. Starting fresh with the new frame."
          />,
          { position: toast.POSITION.BOTTOM_RIGHT, autoClose: 2500 }
        );
      }

      if (isChunked) {
        const success = await handleChunkedImport(data, exportKey, currentSbId);
        if (!success) return;
      } else {
        clearFigmaClipboard();
      }

      const fonts = extractFontsFromNodes(data.nodes);

      if (fonts.length > 0) {
        setExtractedFonts(fonts);
        setPendingFigmaData(data);
        setShowFontsModal(true);
      } else {
        try {
          await dispatchFigmaImport(data, {});
        } catch (err) {
          console.error('Figma import failed:', err);
        }
      }

      return;
    }
    
    const clipboardData = window.localStorage.getItem('CLIPBOARD');
    if (!clipboardData) return;

    const request = JSON.parse(clipboardData);
    const widgets = preparePasteRequest(activePageSection, request);
    if (!widgets) return;

    dispatch(createWidget(editorSession, widgets, activePageSection));
  };

  async function handleChunkedImport(data, exportKey, storyboardId) {

    if (!chunkAccumulator[exportKey]) {
      figmaSession.startChunkSession(exportKey, storyboardId);
      chunkAccumulator[exportKey] = {
        chunks:      {},
        totalChunks: data.totalChunks,
        receivedAt:  Date.now()
      };
      isChunkImportActiveRef.current = true;
      setShowChunkModal(true);
      setChunkProgress({ current: 1, total: data.totalChunks });
    }

    const acc = chunkAccumulator[exportKey];

    if (acc.chunks[data.chunkIndex] !== undefined) {
      toast(
        <FailureToast
          heading="Duplicate Part"
          description="This part was already pasted. Copy the next part from Figma."
        />,
        { position: toast.POSITION.BOTTOM_RIGHT, autoClose: 3500 }
      );
      return false;
    }

    acc.chunks[data.chunkIndex] = data.nodes;
    const receivedCount = Object.keys(acc.chunks).length;
    setChunkProgress({ current: receivedCount, total: acc.totalChunks });

    if (receivedCount < acc.totalChunks) {
      clearFigmaClipboard();
      toast(
        <SuccessToast
          heading="Part Received"
          description={`Part ${receivedCount}/${acc.totalChunks} received. Copy Part ${receivedCount + 1} from Figma.`}
        />,
        { position: toast.POSITION.BOTTOM_RIGHT, autoClose: 1500 }
      );
      return false;
    }

    const mergedNodes = [];
    for (let i = 0; i < acc.totalChunks; i++) {
      const nodes = acc.chunks[i] || [];
      if (i === 0) {
        mergedNodes.push(...nodes);
      } else {
        nodes.filter(n => n.type !== PAGE).forEach(n => mergedNodes.push(n));
      }
    }
    data.nodes = mergedNodes;

    delete chunkAccumulator[exportKey];
    isChunkImportActiveRef.current = false;
    setShowChunkModal(false);

    clearFigmaClipboard();
  
    toast(
      <SuccessToast heading="All Parts Received" description="Processing..." />,
      { position: toast.POSITION.BOTTOM_RIGHT, autoClose: 1500 }
    );

    return true;
  }

  function extractFontsFromNodes(nodes) {
    const fontSet = new Set();
    const fonts = [];

    function extractFromNode(node) {
      if (node.type === TEXT) {
        const desktopStyle = _.get(node, 'properties.desktop');
        const fontFamily = desktopStyle?.fontSize ? 
          _.get(node, 'content', '').match(/font-family:\s*([^;]+)/)?.[1]?.trim() : null;

        if (fontFamily && fontFamily !== 'inherit') {
          const cleanFont = fontFamily.replace(/_/g, ' ');
          if (!fontSet.has(cleanFont)) {
            fontSet.add(cleanFont);
            fonts.push(cleanFont);
          }
        }
      }

      if (node.widgets && node.widgets.length > 0) {
        node.widgets.forEach(extractFromNode);
      }
    }

    nodes.forEach(extractFromNode);
    return fonts;
  }

  const handleFontMappingComplete = async (mappings) => {
    setFontMappings(mappings);
    setShowFontsModal(false);

    if (pendingFigmaData) {
      const payload = pendingFigmaData;
      setPendingFigmaData(null);
      try {
        await dispatchFigmaImport(payload, mappings);
      } catch (err) {
        console.error("Figma import failed:", err);
      }
    }
  };

  async function dispatchFigmaImport(data, fontMappings) {
    figmaSession.dispatchInProgress = true;
  
    try {
      const widgets = data?.nodes || [];

      const pageWidgets      = widgets.filter(item => item.type === PAGE);
      const filteredWidgets  = widgets
        .filter(item => ![PAGE, IMAGE].includes(item.type))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const mediaWidgets     = widgets
        .filter(item => item.type === IMAGE)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      const pageWidgetUid = pageWidgets[0]?.uid;

      filteredWidgets.forEach(widget => applyFontMapping(widget, fontMappings));

      if (pageWidgets[0]) {
        const firstPageWidget  = _.omit(pageWidgets[0], ['order']);
        const pageWidgetsList  = _.orderBy(
          presentWidgets.filter(item => item.type === PAGE), 'order', 'desc'
        );
        const lastPageWidget   = pageWidgetsList[pageWidgetsList.length - 1];
        const newIndex         = pageWidgetsList.findIndex(
          item => item.uid === lastPageWidget?.uid
        ) + 1;

        dispatch(createWidget(editorSession, [firstPageWidget], null));
        dispatch(arrangeWidget(editorSession, firstPageWidget, newIndex));
        dispatch(createWidget(editorSession, filteredWidgets, firstPageWidget.uid));
      }

      await processMediaWidgets(mediaWidgets, pageWidgetUid);

      toast(
        <SuccessToast
          heading="Import Complete"
          description={`Imported ${widgets.length} elements successfully`}
        />,
        { position: toast.POSITION.BOTTOM_RIGHT, autoClose: 3000 }
      );
  
    } finally {
      figmaSession.dispatchInProgress = false;
      figmaSession.endChunkSession();
      clearFigmaClipboard();
    }
  }

  function applyFontMapping(widget, fontMappings) {
    if (widget.type === TEXT && widget.content) {
      const originalFont = widget.content
        .match(/font-family:\s*([^;]+)/)?.[1]?.trim()?.replace(/_/g, ' ');

      if (originalFont && fontMappings[originalFont]) {
        const mappedFont = fontMappings[originalFont];

        widget.content = widget.content.replace(
          /font-family:\s*([^;]+)/g,
          (match, fontName) => {
            const cleanFont = fontName.trim().replace(/_/g, ' ');
            const mappedFont = fontMappings[cleanFont];

            if (mappedFont) {
              if (!widget.fonts) widget.fonts = [];
              widget.fonts.push(mappedFont.id);
              return `font-family: ${mappedFont.fontFamily}`;
            }

            return match;
          }
        );

        if (!widget.fonts) widget.fonts = [];
        widget.fonts.push(mappedFont.id);
      }
    }

    if (widget.widgets && widget.widgets.length > 0) {
      widget.widgets.forEach(w => applyFontMapping(w, fontMappings));
    }
  }

  async function processMediaWidgets(mediaWidgets, pageWidgetUid, limit = 3) {
    let index = 0;

    const workers = new Array(limit).fill(null).map(async () => {
      while (index < mediaWidgets.length) {
        const currentIndex = index++;
        const item = mediaWidgets[currentIndex];

        await new Promise((resolve) => {
          const url = item?.content?.image;
          if (!url) return resolve();

          const desktopLayout = _.get(item, 'properties.desktop.layout');
          const mobileLayout = _.get(item, 'properties.mobile.layout');

          const desktopPosition = _.get(desktopLayout, 'transform.translate');
          const desktopWidth = _.get(desktopLayout, 'width', '200px');
          const desktopHeight = _.get(desktopLayout, 'height');

          const mobilePosition = _.get(mobileLayout, 'transform.translate');
          const mobileWidth = _.get(mobileLayout, 'width', '200px');
          const mobileHeight = _.get(mobileLayout, 'height');

          fetchImageAsFile(url)
            .then(({ file, imgW, imgH }) => {
              file.id = shortid();
              const loadingWidgetId = `widget_loading_${file.id}`;
              const updatedName = removeSpecialCharactersFromName(file.name);

              let finalDesktopHeight = desktopHeight;
              let finalMobileHeight = mobileHeight;

              if (!desktopHeight || desktopHeight === "auto") {
                const dWidth = parseFloat(desktopWidth);
                if (imgW && imgH && dWidth) {
                  finalDesktopHeight = `${(imgH / imgW) * dWidth}px`;
                }
              }

              if (!mobileHeight || mobileHeight === "auto") {
                const mWidth = parseFloat(mobileWidth);
                if (imgW && imgH && mWidth) {
                  finalMobileHeight = `${(imgH / imgW) * mWidth}px`;
                }
              }

              const loadingWidget = {
                type: 'cleverstory.v2.widgets.image',
                name: 'Image',
                uid: loadingWidgetId,
                parent: pageWidgetUid,
                order: item.order,
                properties: {
                  desktop: {
                    visible: true,
                    layout: {
                      transform: { translate: desktopPosition, rotate: '0deg' },
                      width: desktopWidth,
                      height: finalDesktopHeight
                    }
                  },
                  mobile: {
                    visible: true,
                    layout: {
                      transform: { translate: mobilePosition, rotate: '0deg' },
                      width: mobileWidth,
                      height: finalMobileHeight
                    }
                  }
                },
                content: null
              };

              dispatch(createWidget(editorSession, [loadingWidget], pageWidgetUid, true, true));

              const s3uploader = new S3Upload({
                signingUrl: '/file_upload/initiate',
                signingUrlMethod: 'POST',
                signingUrlHeaders: { Authorization: `Bearer ${AuthService.getAccessToken()}` },
                server: API_URL,
                scrubFilename: (filename) =>
                  removeSpecialCharactersFromName(filename).replace(/[^\w\d_\-.\s]+/gi, ''),
                contentDisposition: 'auto',
                signingUrlQueryParams: { originalName: encodeURIComponent(updatedName) },
                preprocess: handleFileCheck,
                uploadRequestHeaders: {},
                files: [file],
                onFinishS3Put: (info, uploadedFile) => {
                  handleUploadComplete(
                    info,
                    uploadedFile,
                    loadingWidgetId,
                    desktopPosition,
                    mobilePosition,
                    item,
                    pageWidgetUid
                  );
                  resolve();
                },
                onProgress: handleUploadProgress,
                onError: () => {
                  dispatch(deleteWidgets(editorSession, [loadingWidgetId]));
                  resolve();
                }
              });

              handleUploadStart([s3uploader]);
            })
            .catch(() => resolve());
        });
      }
    });

    await Promise.all(workers);
  }

  const handleChunkModalClose = () => {
    setShowChunkModal(false);
    Object.keys(chunkAccumulator).forEach(key => delete chunkAccumulator[key]);
    setChunkProgress({ current: 0, total: 0 });
    setPendingFigmaData(null);
    isChunkImportActiveRef.current = false;
    figmaSession.endChunkSession(); 
  };

  const handleFontsModalDiscard = () => {
    setShowFontsModal(false);
    setPendingFigmaData(null);
    handleChunkModalClose();
  };

  const handleCopyAnimation = ({ exit }) => {
    const request = {
      type: 'copy', 
      exit,
      storyboardId: _.get(storyboardContext, 'id'),
      workspaceId: _.get(workspace, 'id', null),
      editorSession: editorContext.editorSession.id, 
    };

    dispatch(copyPasteAnimation(request, (type) => {
      if(type === 'copy') {
        toast(
          <SuccessToast
            heading={`Success`}
            description={`Animation copied.`}
          />,
          {
            type: toast.TYPE.DEFAULT,
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 3000,
          }
        );
      }
    }));
  };
  
  const handlePasteAnimation = ({ exit }) => {
    const request = {
      type: 'paste',
      exit,
      editorSession: editorContext.editorSession.id,
    };

    dispatch(copyPasteAnimation(request, (type) => {
      if(type === 'paste') {
        toast(
          <SuccessToast
            heading={`Success`}
            description={`Animation pasted.`}
          />,
          {
            type: toast.TYPE.DEFAULT,
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 3000,
          }
        );
      }
    }));
  };

  const prepareCopyRequest = () => {
    const scrollContainer = document.querySelector(`#${editorContext.editorSession.id} .editor_widget_scroll_container`);
    const el = document.getElementById(parentWidget?.uid);
    const parentWidgetRect = getElementOffsetTop(el);

    function getElementOffsetTop(element) {
      let top = 0;
      while (element) {
        top += element.offsetTop;
        element = element.offsetParent;
      }
      return top - 255;
    }

    var request = {
      storyboard: _.get(storyboardContext, 'id'),
      storyboardType: _.get(storyboardContext, 'type'),
      parentWidgetRect: parentWidgetRect,
      workspace: _.get(workspace, 'id', null),
      scrollPosition: {
        top: scrollContainer.scrollTop,
        left: scrollContainer.scrollLeft
      },
      activePageSection,
      pageId
    }

    return request;
  }

  const preparePasteRequest = (pageWidget, request) => {
    const scrollContainer = document.querySelector(`#${editorContext.editorSession.id} .editor_widget_scroll_container`);
    const top = scrollContainer.scrollTop;
    const left = scrollContainer.scrollLeft;
    const storyboardType = _.get(storyboardContext, 'type');
    const currentStoryboardId = _.get(storyboardContext, 'id');
    const variableMappingsIgnoreTypes = ['LIVE_ELEMENT', 'GATING', 'PALETTE_COMPONENT'];
    const originalTop = _.get(request, 'scrollPosition.top', 0);
    const originalLeft = _.get(request, 'scrollPosition.left', 0);
    const copiedStoryboard = _.get(request, 'storyboardType');
    const parentWidgetRect = _.get(request, 'parentWidgetRect');
    
    const currentScrollPosition = {
      top: top,
      left: left
    }

    var lastPastedPage = pasteStateReference.current.page;
    var offset = pasteStateReference.current.offset;
    if(lastPastedPage != pageWidget || viewportPositionChanged(currentScrollPosition, pasteStateReference.current.scrollPosition)){
      offset = 0;
    }

    var widgets = request.widgets || [];
   
    var ignoreTypes = [];
    switch (true) {
      case entityType === 'GATING':
        ignoreTypes = [MENU, SEEK, CONTENT, SECTION, SECTION_COLUMN, ASSET, CAROUSEL, ANCHOR]
        break;
      case entityType === 'LIVE_ELEMENT':
        ignoreTypes = [...ignoreTypes, LIVE_ELEMENT, CONTENT, CAROUSEL, ANCHOR, SEEK, ASSET];
        break;
      case entityType === 'PALETTE_COMPONENT':
        ignoreTypes = [ANCHOR]
        break;
      case storyboardType === STORYBOARD_TYPES.INTERACTIVE_CONTENT:
        ignoreTypes = [MENU];
        if (request.widgets[0] && request.widgets[0].properties[breakpoint].stretched) {
          ignoreTypes = [...ignoreTypes, request.widgets[0].type];
        }
        break;
      case storyboardType === STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2:
        ignoreTypes = [MENU];
        const sourceWidget = request.widgets[0];
        if (sourceWidget) {
          const isSourceStretched = sourceWidget.properties[breakpoint].stretched;
          if (isSourceStretched && ![SECTION, CONTENT].includes(sourceWidget.type)) {
            ignoreTypes = [...ignoreTypes, sourceWidget.type];
          }
        }
        break;
      case SLIDE_STORYBOARDS.includes(storyboardType):
        ignoreTypes = [MENU, SEEK, CONTENT, SECTION_COLUMN, ANCHOR, ];
        if (request.widgets[0] && request.widgets[0].properties[breakpoint].stretched) {
          ignoreTypes = [...ignoreTypes, request.widgets[0].type];
        }
        break;
      default:
        break;
    }
    
    if (workspace.id !== request.workspace) {
      ignoreTypes = [...ignoreTypes, LIVE_ELEMENT]
    }
    
    widgets = widgets.filter(item => !_.includes(ignoreTypes, item.type));

    widgets = widgets.map(widget => {
      const allowedActions = ['link', 'pageAnchor'];
      const getActionType = (widget) => {
        const actions = _.get(widget, 'actions' , []);
        const actionType = _.last(actions)?.type;
        return actionType;
      }
      let children = _.get(widget, 'widgets', null);
      let hotspot = children && children.find(item => _.isEqual(HOTSPOT, item.type));

      if (hotspot && entityType === 'GATING'){
        const hotspotChildren = _.get(hotspot, 'widgets', null);
        if (_.size(hotspotChildren) > 0){
          const carouselChild = hotspotChildren.filter(item => item.type === CAROUSEL);
          if (carouselChild){
            toast(
              <FailureToast
                heading="Pasting Failed"
                description="Hotspot with a carousel isn’t supported in gating screens."
              />,
              {
                type: toast.TYPE.DEFAULT,
                position: toast.POSITION.BOTTOM_RIGHT,
                autoClose: 3000
              }
            );
            return;
          } 
        }
      }

      if(children != null && !hotspot) {
        children = children.map(childwidget => {
          if(_.get(childwidget, 'actions') && _.get(childwidget, 'actions') !== null) {
            if(_.get(request, 'storyboard') != _.get(storyboardContext, 'id')) {
              const updatedWidget = {
                ...childwidget,
                actions: allowedActions.includes(getActionType(childwidget)) ? childwidget.actions :  []
              }

              return updatedWidget;
            }
          }
          return childwidget;
        })

        widget = {
          ...widget,
          widgets: children
        }
        return widget;

      } else {
        if(_.get(widget, 'actions') && _.get(widget, 'actions') !== null) {
          if(_.get(request, 'storyboard') != _.get(storyboardContext, 'id')) {
            widget = {
              ...widget,
              actions: allowedActions.includes(getActionType(widget)) ? widget.actions : [],
            }

            if (widget.type === TEXT){
              widget = {
                ...widget,
                content: allowedActions.includes(getActionType(widget)) ? widget.content : widget.content.replace(/<a[^>]*>(.*?)<\/a>/g, '$1')
              };
            }

            return widget;
          }
        }
        return widget;
      }
    })

    if(_.size(widgets) === 0){
      return null;
    }

    var processedWidgets = widgets.map(widget => {
      var layout = _.get(widget, 'properties.desktop.layout') || {};
      var stretched = _.get(widget, 'properties.desktop.stretched') || null;
      var inlineStretched = _.get(widget, 'properties.desktop.display') || null;
      const positon = new Frame(layout);
      const value = positon.get("transform", "translate").split(",").map(str => {
        str = str.trim();
        return parseFloat(str);
      });
      if (storyboardType === 'INTERACTIVE_CONTENT' && (stretched || inlineStretched === 'STRETCHED') && widget.type !== CONTENT) {
        return;
      }
      if (storyboardType === STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2 && (stretched || inlineStretched === 'STRETCHED')) {
        if (![SECTION, CONTENT].includes(widget.type)) return;
      }

      const actionType = _.get(widget, 'actions.0.type',null);
      if (actionType && actionType === 'page' && entityType === 'GATING'){
        widget.actions = [];
        if (widget.content && typeof widget.content === 'string' && widget.type === TEXT) {
          widget.content = widget.content.replace(/<a[^>]*>(.*?)<\/a>/g, '$1');
        }
      }

      const cleanWidgetActions = (widget, options = {}) => {
        const {
          allowedActions = [],
          disallowedActions = [],
          cleanTextLinks = true,
        } = options;
      
        const cleanActions = (w) => {
          if (!w) return;

          const actions = w.actions || [];
          const actionType = _.last(actions)?.type;
      
          // Clean TEXT widget <a> tags if the action isn't allowed or is disallowed
          if (
            w.type === TEXT &&
            cleanTextLinks &&
            ((allowedActions.length && !allowedActions.includes(actionType)) ||
              (disallowedActions.length && disallowedActions.includes(actionType)))
          ) {
            w.content = w.content.replace(/<a[^>]*>(.*?)<\/a>/g, '$1');
          }
      
          // Remove actions based on allowed/disallowed list
          if (w.actions) {
            if (
              (allowedActions.length && !allowedActions.includes(actionType)) ||
              (disallowedActions.length && disallowedActions.includes(actionType))
            ) {
              w.actions = [];
            }
          }
        };
      
        const cleanMenuActions = (menuWidget) => {
          if (!menuWidget) return;
      
          const actionType = menuWidget.action?.type;
          if (
            menuWidget.action &&
            ((allowedActions.length && !allowedActions.includes(actionType)) ||
              (disallowedActions.length && disallowedActions.includes(actionType)))
          ) {
            menuWidget.action = null;
          }
      
          if (_.size(menuWidget.children) > 0) {
            menuWidget.children.forEach(cleanMenuActions);
          }
        };

        if(widget.actions) {
          cleanActions(widget);
        }

        widget?.widgets?.forEach((child) => {
          cleanActions(child);

          if (child.type === MENU_CONTAINER || child.type === MENU_DRAGGABLE_CONTAINER) {
            child.content?.forEach(cleanMenuActions);
          }
      
          child.widgets?.forEach(cleanActions);
        });
      };
      
      if (request?.storyboard !== currentStoryboardId) {
        cleanWidgetActions(widget, {
          allowedActions: ['link', 'pageAnchor'],
        });
      }

      if ((request.pageId && request.pageId !== pageId) || (request?.activePageSection !== pageWidget && storyboardType === 'INTERACTIVE_CONTENT')) {
        cleanWidgetActions(widget, {
          disallowedActions: ['carousel'],
        });
      }

      if (variableMappingsIgnoreTypes.includes(entityType) || request?.storyboard !== currentStoryboardId){
        removeVariableMappingsFromWidget(widget);
      }

      const clearLLMProperty = (widget, currentStoryboardId, request) => {
        if (!widget) return;
      
        if (widget?.properties?.desktop?.llmProperty && request?.storyboard !== currentStoryboardId) {
          widget.properties.desktop.llmProperty = null;
        }
      
        if (Array.isArray(widget.widgets)) {
          widget.widgets.forEach((child) =>
            clearLLMProperty(child, currentStoryboardId, request)
          );
        }
      };

      clearLLMProperty(widget, currentStoryboardId, request);

      if(widget.type === ASSET){
        widget.content = null;
        
        if(widget.properties.desktop.type && _.includes(["VIDEO"], widget.properties.desktop.type)){
          widget.properties.desktop.video = null;
        }

        if(widget.properties.desktop.background && _.includes(["image", "video"], widget.properties.desktop.background.type)){
          widget.properties.desktop.background = null;
        }
      }

      if(widget.type === CONTENT){
        delete widget.id;
        widget.content = {};

        widget.properties.desktop.filters = false;
        widget.properties.desktop.search = false;
        widget.properties.mobile.filters = false;
        widget.properties.mobile.search = false;

        widget.properties.desktop.layoutStyle.itemsPerPage = 3;
        
        var contentWidgets = widget.widgets.filter(item => !_.includes([CONTENT_CARD], item.type));

        contentWidgets.map(child => {
          if([CONTENT_FILTER].includes(child.type)){
              var filterChildren = child.widgets.map(filterChild => {
                if(filterChild.type === CONTENT_FILTER_CONTAINER){
                  filterChild.content = [];
                }
                if(filterChild.type === CONTENT_SEARCH){
                  filterChild.content = null
                }
                return filterChild;
              });
              child.widgets = filterChildren;
            }
          })

        widget.widgets = contentWidgets;

        if(widget.properties.desktop.background && _.includes(["image", "video"], widget.properties.desktop.background.type)){
          widget.properties.desktop.background = null;
        }
      }

      isAssetIsChild(widget);

      function getElementOffsetTop(element) {
        let top = 0;
        while (element) {
          top += element.offsetTop;
          element = element.offsetParent;
        }
        return top - 255;
      }

      if(entityType === "GATING"){
        const x = value[0] - originalLeft + left + 20 * offset;
        const y = value[1] - originalTop + top  + 20 * offset;
        positon.set('transform', 'translate', `${x}px, ${y}px`);
      }

      else  {
        if (PAGE_CONTAIN_STORYBOARDS.includes(storyboardType)) {
          const x = value[0] - originalLeft + left;
          let y = value[1] - originalTop + top + 20 * offset;

          if(SEGMENT_STORYBOARDS.includes(copiedStoryboard)){
            y = y + parentWidgetRect;
          }
          
          positon.set('transform', 'translate', `${x}px, ${y}px`);
        }

        if(SEGMENT_STORYBOARDS.includes(storyboardType)){
        const selectPageContainer = document.querySelector(`#${editorContext.editorSession.id} .editor_widget_scroll_container .editor-page-wrapper #${activePageSection}`);
        const pageRect = selectPageContainer?.getBoundingClientRect();

        let pageoffset = 0;
      
        if(activePageSection && selectPageContainer){ 
          pageoffset = scrollContainer.scrollTop -  getElementOffsetTop(selectPageContainer) ;
        }
        const x = value[0] - originalLeft + left + 0 * offset;
        let y = value[1] + 40 * offset;

          if (activePageSection && (pageRect.height < y || y < 0)) {
            y = pageRect.height / 2;
          }
        positon.set('transform', 'translate', `${x}px, ${y}px`);
      }
    }

      widget = _.set(widget, 'properties.desktop.layout', positon.get());
      return widget;

    }).sort((w1, w2) => {
      const o1 = _.get(w1, 'order') || 1;
      const o2 = _.get(w2, 'order') || 1;
  
      return o1 - o2;
    });

    pasteStateReference.current = {
      page: pageWidget,
      scrollPosition: currentScrollPosition,
      offset: offset + 1
    };

    return processedWidgets;
  }

  const viewportPositionChanged = (current, previous) => {
    if(!previous){
      return null;
    }

    const currentTop = _.get(current, 'top', 0);
    const currentLeft = _.get(current, 'left', 0);

    const previousTop = _.get(previous, 'top', 0);
    const previousLeft = _.get(previous, 'left', 0);

    return (currentTop != previousTop || currentLeft != previousLeft);
  }

  return (
    <>
      <ChunkProgressModal
        show={showChunkModal}
        currentChunk={chunkProgress.current}
        totalChunks={chunkProgress.total}
        onClose={handleChunkModalClose}
      />

      <FigmaImportFontModal
        show={showFontsModal}
        figmaFonts={extractedFonts}
        onComplete={handleFontMappingComplete}
        onDiscard={handleFontsModalDiscard}
      />
    </>
  );
}

const widgetTypesWithAsset = new Set([SECTION, CAROUSEL, IMAGE, VIDEO, BUTTON, SHAPE, LINE]);

function clearAssetContent(widget) {
  if (!widget || !widget.widgets) return;


  widget.widgets.forEach(child => {
    if (child?.type === ASSET) {
      child.content = null;
    } else if (child?.widgets) {
      child.widgets?.forEach(subChild => {
        if (subChild?.type === CAROUSEL_CARD) {
          subChild.widgets?.forEach(carouselChild => {
            if (carouselChild?.type === ASSET) {
              carouselChild.content = null;
            }
          });
        }
      });
    }
  });
}

function isAssetIsChild(widget) {
  if (widgetTypesWithAsset.has(widget?.type)) {
    widget.widgets?.forEach(child => {
      clearAssetContent(child);
    });
  }
}


function removeVariableMappingsFromWidget(request) {
    if(!request){
      return request;
    }

    if (request.type === TEXT){
      let content = request.content;
      content = content.replace(
        /<span[^>]*class="[^"]*\bvariable-wrapper\b[^"]*"[^>]*>(.*?)<\/span>/g,
        "$1"
      );
      content = content.replace(
        /<span[^>]*\sdata-variable-id=["'][^"']*["'][^>]*>(.*?)<\/span>/g,
        "$1"
      );      
      request.content = content;
      request.properties.desktop.variables = null;
    }

    const variableMappings = request.variableMappings;
    if (variableMappings && _.size(variableMappings) > 0) {
      request.variableMappings = [];
    }

    if(_.size(request.widgets) > 0){
      request.widgets.forEach(childRequest => {
        removeVariableMappingsFromWidget(childRequest);
      });
    }
}

export async function fetchImageAsFile(imageUrl, fileName = 'image.png') {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], fileName, { 
            type: blob.type || 'image/png',
            lastModified: Date.now()
          });

          resolve({
            file,
            imgW: img.width,
            imgH: img.height
          });

        } else {
          reject(new Error('Failed to create blob'));
        }
      }, 'image/png', 0.95);
    };
    
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = imageUrl;
  });
}